import {
	useVirtualizer,
	type PartialKeys,
	type VirtualizerOptions
} from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef } from 'react';

type VirtualOptionsProps<T extends Element> = Omit<
	PartialKeys<
		VirtualizerOptions<T, Element>,
		'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
	>,
	'getScrollElement'
>;

type UseVirtualElementsProps<T extends Element> = VirtualOptionsProps<T> & {
	isBusy?: boolean;
	hasMore?: boolean;
	onBottomReached?: () => void;
};

export const useVirtualElements = <T extends Element>({
	onBottomReached,
	isBusy = false,
	hasMore = false,
	...virtualOptions
}: UseVirtualElementsProps<T>) => {
	const containerRef = useRef<T>(null);

	const virtualizer = useVirtualizer<T, Element>({
		...virtualOptions,
		initialRect: {
			width: 100,
			height: 1000
		},
		getScrollElement: () => {
			if (
				containerRef.current?.clientHeight &&
				containerRef.current?.clientWidth
			) {
				return containerRef.current;
			}

			return null;
		}
	});

	const fetchMoreOnBottomReached = useCallback(
		(containerRefElement?: T | null) => {
			if (containerRefElement && hasMore) {
				const { scrollHeight, scrollTop, clientHeight } =
					containerRefElement;
				const isCloserToBottom =
					scrollHeight - scrollTop - clientHeight < 300;

				const shouldFetchMore = isCloserToBottom && !isBusy;

				if (shouldFetchMore) {
					onBottomReached?.();
				}
			}
		},
		[hasMore, isBusy, onBottomReached]
	);

	const virtualRows = virtualizer.getVirtualItems();

	const [paddingTop, paddingBottom] = useMemo(() => {
		let padTop = 0;
		let padBottom = 0;

		if (virtualRows.length > 0) {
			const firstRow = virtualRows.at(0);
			const lastRow = virtualRows.at(-1);

			padTop = Math.max(
				0,
				(firstRow?.start || 0) - virtualizer.options.scrollMargin
			);

			padBottom = Math.max(
				0,
				virtualizer.getTotalSize() - (lastRow?.end || 0)
			);
		}

		return [padTop, padBottom];
	}, [virtualRows, virtualizer]);

	useEffect(() => {
		fetchMoreOnBottomReached(containerRef.current);
	}, [fetchMoreOnBottomReached]);

	return {
		containerRef,
		virtualizer,
		onScroll: fetchMoreOnBottomReached,
		virtualRows,
		paddingTop,
		paddingBottom
	};
};
